import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { Clause } from '@shared/types';

interface ClauseListProps {
  clauses: Clause[];
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  typeFilter: string;
  onTypeFilterChange: (value: string) => void;
  approvedFilter: boolean | undefined;
  onApprovedFilterChange: (value: boolean | undefined) => void;
  onCreateNew: () => void;
  onEdit: (clause: Clause) => void;
  onDelete: (id: string) => void;
}

export function ClauseList({
  clauses,
  loading,
  search,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  approvedFilter,
  onApprovedFilterChange,
  onCreateNew,
  onEdit,
  onDelete,
}: ClauseListProps) {
  return (
    <div className="bg-white shadow-sm rounded-lg">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Clauses</h2>
          <button
            onClick={onCreateNew}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            <PlusIcon className="w-5 h-5" />
            New Clause
          </button>
        </div>

        <input
          type="text"
          placeholder="Search clauses..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 mb-3"
        />

        <div className="flex gap-3">
          <select
            value={typeFilter}
            onChange={(e) => onTypeFilterChange(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Types</option>
            <option value="General">General</option>
            <option value="Payment">Payment</option>
            <option value="Termination">Termination</option>
            <option value="Confidentiality">Confidentiality</option>
            <option value="Liability">Liability</option>
          </select>

          <select
            value={approvedFilter === undefined ? '' : approvedFilter.toString()}
            onChange={(e) =>
              onApprovedFilterChange(
                e.target.value === '' ? undefined : e.target.value === 'true'
              )
            }
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Status</option>
            <option value="true">Approved</option>
            <option value="false">Draft</option>
          </select>
        </div>
      </div>

      <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : clauses.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No clauses found</div>
        ) : (
          clauses.map((clause) => (
            <div
              key={clause.id}
              className="p-4 hover:bg-gray-50 cursor-pointer"
              onClick={() => onEdit(clause)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900">{clause.title}</h3>
                    {clause.approved && (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                        Approved
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{clause.type}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {clause.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                    {clause.tags.length > 3 && (
                      <span className="text-xs text-gray-500">
                        +{clause.tags.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(clause);
                    }}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-md"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(clause.id);
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

